import { useCallback, useRef, useState } from "react";

import { type BuilderTimeSlot, type FormValues, type MeetingTimeSlot } from "./AddEventTimesModal.types";
import { isMeetingTimeComplete } from "./AddEventTimesModal.utils";

export const useTimeSlots = () => {
  const [timeSlots, setTimeSlots] = useState<BuilderTimeSlot[]>([{ id: "0" }]);
  const [nextId, setNextId] = useState(1);

  const addTimeSlot = useCallback(() => {
    const newId = String(nextId);
    setTimeSlots((previous) => [...previous, { id: newId }]);
    setNextId((previous) => previous + 1);
    return newId;
  }, [nextId]);

  const removeTimeSlot = useCallback((idToRemove: string) => {
    setTimeSlots((previous) => previous.filter((slot) => slot.id !== idToRemove));
  }, []);

  const resetTimeSlots = useCallback(() => {
    setTimeSlots([{ id: "0" }]);
    setNextId(1);
  }, []);

  const replaceTimeSlots = useCallback((count: number) => {
    const newSlots: BuilderTimeSlot[] = Array.from({ length: count }, (_, index) => ({
      id: String(index),
    }));
    setTimeSlots(newSlots);
    setNextId(count);
    return newSlots;
  }, []);

  return {
    addTimeSlot,
    removeTimeSlot,
    replaceTimeSlots,
    resetTimeSlots,
    timeSlots,
  };
};

export const useMeetingTimeSlots = () => {
  const [meetingTimeSlots, setMeetingTimeSlots] = useState<MeetingTimeSlot[]>([]);
  const [nextId, setNextId] = useState(0);

  const addMeetingTimeSlot = useCallback(() => {
    const newId = String(nextId);
    setMeetingTimeSlots((previous) => [...previous, { displayIndex: previous.length + 1, id: newId }]);
    setNextId((previous) => previous + 1);
    return newId;
  }, [nextId]);

  const removeMeetingTimeSlot = useCallback((idToRemove: string) => {
    setMeetingTimeSlots((previous) => {
      const filtered = previous.filter((slot) => slot.id !== idToRemove);
      return filtered.map((slot, index) => ({ ...slot, displayIndex: index + 1 }));
    });
  }, []);

  const replaceMeetingTimeSlots = useCallback((count: number) => {
    const newSlots: MeetingTimeSlot[] = Array.from({ length: count }, (_, index) => ({
      displayIndex: index + 1,
      id: String(index),
    }));
    setMeetingTimeSlots(newSlots);
    setNextId(count);
    return newSlots;
  }, []);

  const resetSlots = useCallback(() => {
    setMeetingTimeSlots([]);
    setNextId(0);
  }, []);

  return {
    addMeetingTimeSlot,
    meetingTimeSlots,
    removeMeetingTimeSlot,
    replaceMeetingTimeSlots,
    resetSlots,
  };
};

export const useSlotExpansion = () => {
  const [expandedSlots, setExpandedSlots] = useState<Set<string>>(new Set());

  const toggleSlotExpansion = useCallback((slotId: string) => {
    setExpandedSlots((previous) => {
      const next = new Set(previous);
      if (next.has(slotId)) {
        next.delete(slotId);
      } else {
        next.add(slotId);
      }
      return next;
    });
  }, []);

  const expandSlot = useCallback((slotId: string) => {
    setExpandedSlots((previous) => new Set(previous).add(slotId));
  }, []);

  const collapseSlot = useCallback((slotId: string) => {
    setExpandedSlots((previous) => {
      const next = new Set(previous);
      next.delete(slotId);
      return next;
    });
  }, []);

  const resetExpansion = useCallback(() => {
    setExpandedSlots(new Set());
  }, []);

  const cleanupSlotExpansion = useCallback((slotId: string) => {
    setExpandedSlots((previous) => {
      const next = new Set(previous);
      next.delete(slotId);
      return next;
    });
  }, []);

  return {
    cleanupSlotExpansion,
    collapseSlot,
    expandSlot,
    expandedSlots,
    resetExpansion,
    toggleSlotExpansion,
  };
};

export const useMeetingTimeFieldBlur = () => {
  const autoCollapseTimeoutRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const handleFieldFocus = useCallback((slotId: string) => {
    const existingTimeout = autoCollapseTimeoutRef.current.get(slotId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      autoCollapseTimeoutRef.current.delete(slotId);
    }
  }, []);

  const handleFieldBlur = useCallback(
    (slotId: string, formValues: FormValues, collapseSlot: (slotId: string) => void, expandedSlots: Set<string>) => {
      const existingTimeout = autoCollapseTimeoutRef.current.get(slotId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      const timeoutId = setTimeout(() => {
        if (isMeetingTimeComplete(slotId, formValues) && expandedSlots.has(slotId)) {
          collapseSlot(slotId);
        }
        autoCollapseTimeoutRef.current.delete(slotId);
      }, 1000);

      autoCollapseTimeoutRef.current.set(slotId, timeoutId);
    },
    [],
  );

  const clearTimeouts = useCallback(() => {
    autoCollapseTimeoutRef.current.forEach(clearTimeout);
    autoCollapseTimeoutRef.current.clear();
  }, []);

  return {
    clearTimeouts,
    handleFieldBlur,
    handleFieldFocus,
  };
};
